<script lang="ts" module>
	export type TopBarClasses = {
		moreLink?: string;
		moreLinkClasses?: MobileMenuClasses;
		socials?: string;
		links?: string;
	};

	export interface TopBarProps {
		showGlobalNav?: boolean | undefined;
		pathname: string;
		navigating: boolean;
		homeRoute: string;
		siteLinks?: SiteNavLink[];
		globalLinks?: GlobalNavLink[];
		dark?: boolean | undefined;
		isSearchEnabled?: boolean | undefined;
		onClickSearch?: (() => void) | undefined;
		/** Forwarded to MobileMenu: show the "Menu" text next to the hamburger. */
		showMenuLabel?: boolean | undefined;
		classes?: TopBarClasses;
		class?: string;
		homeIcon?: import('svelte').Snippet;
		leftLink?: import('svelte').Snippet;
		rightLink?: import('svelte').Snippet;
		copyright?: import('svelte').Snippet;
	}
</script>

<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { HeniLogoMinimal } from '../../../../assets/logos/HeniLogoMinimal';
	import { IconLink } from '../../../../components/IconLink';
	import { IndeterminateProgressBar } from '../../../../components/IndeterminateProgressBar';
	import { toKebabCase } from '../../../../utils/toKebabCase';
	import type { GlobalNavLink, SiteNavLink } from '../../types';
	import { NavLink } from '../NavLink';
	import { Socials } from '../Socials';
	import { MobileMenu } from './MobileMenu';
	import type { MobileMenuClasses } from './MobileMenu/MobileMenu.svelte';

	let {
		showGlobalNav = true,
		pathname,
		navigating,
		homeRoute,
		siteLinks = [],
		globalLinks = [],
		dark = undefined,
		isSearchEnabled = undefined,
		onClickSearch = undefined,
		showMenuLabel = false,
		classes = {},
		...rest
	}: TopBarProps = $props();

	const dataCyPrefix = 'header-top-bar';
</script>

<div class={twMerge('flex h-full w-full items-center justify-between', rest.class)}>
	<IconLink
		dataCy={`${dataCyPrefix}-home`}
		href={homeRoute}
		label="See HENI home page"
		class="hidden lg:block"
	>
		<HeniLogoMinimal class="h-[22px] w-[48px]" />
	</IconLink>
	<!-- Site logos are drawn scaled down below `xs`, so their intrinsic width is wider
	     than what is painted. Giving the logo whatever space the actions leave — rather
	     than a fixed width — keeps the actions on screen on narrow phones. -->
	<div class="block min-w-0 flex-1 lg:hidden">
		{@render rest?.homeIcon?.()}
	</div>

	<div class={twMerge('flex h-full shrink-0', classes?.links)}>
		<div class="hidden lg:flex gap-8">
			{#each globalLinks as { label, href, onClick, items, isLive }}
				<NavLink
					{label}
					dataCy={`${dataCyPrefix}-${toKebabCase(label)}-link`}
					{href}
					onclick={onClick as () => void}
					{items}
					{isLive}
					{pathname}
				/>
			{/each}
		</div>

		<MobileMenu
			{showGlobalNav}
			{pathname}
			{onClickSearch}
			{isSearchEnabled}
			{dark}
			{showMenuLabel}
			{siteLinks}
			{globalLinks}
			class={classes?.moreLink}
			classes={classes?.moreLinkClasses}
		>
			{#snippet copyright()}
				{@render rest?.copyright?.()}
			{/snippet}
			{#snippet homeIcon()}
				{@render rest?.homeIcon?.()}
			{/snippet}
			{#snippet leftLink()}
				{@render rest?.leftLink?.()}
			{/snippet}
			{#snippet rightLink()}
				{@render rest?.rightLink?.()}
			{/snippet}
		</MobileMenu>

		<Socials
			dataCy={dataCyPrefix}
			class={twMerge('ml-6 hidden space-x-6 lg:flex', classes.socials)}
			iconProps={{
				class: 'h-[1.25rem] w-[1.25rem]'
			}}
		/>
	</div>
</div>

{#if navigating}
	<IndeterminateProgressBar
		dataCy={dataCyPrefix}
		class="absolute bottom-[-1px] left-[-1rem] w-[calc(100vw+1rem)] lg:hidden"
	/>
{/if}
